import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'dropdown',
    'someoneElse',
    'identityWrap',
    'identityInput',
    'customField',
  ]

  static values = { fieldValues: Object, selfSentinel: String }

  connect() {
    this._onBuyerInput = this._onBuyerInput.bind(this)
    document.addEventListener('input', this._onBuyerInput)
    this._toggle()
    this._seedIdentityDirty()
    this._syncIdentity(true)
  }

  disconnect() {
    document.removeEventListener('input', this._onBuyerInput)
  }

  markIdentityDirty(event) {
    event.target.dataset.identityDirty = 'true'
  }

  select() {
    this._clearIdentityDirty()
    this._toggle()
    this._repopulate()
    this._syncIdentity(false)
  }

  _onBuyerInput(event) {
    const source = event.target.closest?.('[data-buyer-identity-field]')
    if (!source || !this._isSelf()) return
    this._syncIdentity(false)
  }

  _clearIdentityDirty() {
    if (!this.hasIdentityInputTarget) return
    this.identityInputTargets.forEach(
      (input) => delete input.dataset.identityDirty,
    )
  }

  _seedIdentityDirty() {
    if (!this.hasIdentityWrapTarget) return
    const buyer = this._buyerValues()
    this.identityWrapTargets.forEach((wrap) => {
      const input = this._inputFor(wrap)
      if (!input || input.value.length === 0) return
      if (input.value === buyer[wrap.dataset.identityField]) return
      input.dataset.identityDirty = 'true'
    })
  }

  _toggle() {
    if (!this.hasSomeoneElseTarget) return
    const someoneElse = this._isSomeoneElse()
    this.someoneElseTargets.forEach((block) =>
      block.classList.toggle('hidden', !someoneElse),
    )
  }

  _repopulate() {
    if (!this.hasCustomFieldTarget) return
    const values = this._selectedValues()
    this.customFieldTargets.forEach((field) => {
      this._applyFieldValue(field, values[field.dataset.dfCode])
    })
  }

  _applyFieldValue(field, value) {
    switch (field.dataset.dfType) {
      case 'string':
      case 'text':
      case 'date': {
        field
          .querySelectorAll('input, textarea')
          .forEach((input) => (input.value = value ?? ''))
        break
      }
      case 'dropdown': {
        field
          .querySelectorAll('select')
          .forEach((select) => (select.value = value ?? ''))
        break
      }
      case 'radio_group': {
        field.querySelectorAll('input[type=radio]').forEach((radio) => {
          radio.checked =
            value !== null &&
            value !== undefined &&
            radio.value === String(value)
        })
        break
      }
      case 'checkbox_group': {
        const selected = Array.isArray(value) ? value.map(String) : []
        field.querySelectorAll('input[type=checkbox]').forEach((box) => {
          box.checked = selected.includes(box.value)
        })
        break
      }
      case 'boolean': {
        field.querySelectorAll('input[type=checkbox]').forEach((box) => {
          box.checked = Boolean(value)
        })
        break
      }
    }
  }

  _syncIdentity(initial) {
    if (!this.hasIdentityWrapTarget) return

    const someoneElse = this._isSomeoneElse()
    const values = this._selectedValues()

    this.identityWrapTargets.forEach((wrap) => {
      const field = wrap.dataset.identityField
      const collected = wrap.dataset.identityCollected === 'true'
      const staticRequired = wrap.dataset.identityRequired === 'true'
      const forceRequired = someoneElse && field === 'first_name'
      const input = this._inputFor(wrap)
      if (!input) return

      const keepServerValue = initial && input.value.length > 0

      if (someoneElse) {
        wrap.classList.remove('hidden')
        if (!keepServerValue) input.value = ''
        input.readOnly = false
      } else if (collected) {
        wrap.classList.remove('hidden')
        const dirty = this._isSelf() && input.dataset.identityDirty === 'true'
        if (!keepServerValue && !dirty) {
          const value = values[field]
          input.value = value === null || value === undefined ? '' : value
        }
        input.readOnly = false
      } else {
        wrap.classList.add('hidden')
        input.value = ''
        input.readOnly = false
      }

      // Chrome refuses to submit a required hidden input ("not focusable").
      input.required = wrap.classList.contains('hidden')
        ? false
        : staticRequired || forceRequired

      const marker = wrap.querySelector('[data-required-marker]')
      if (marker) marker.classList.toggle('hidden', !input.required)
    })

    this._syncNameSpan()
    this._syncIdentityGrid()
  }

  _syncIdentityGrid() {
    const grid = this.identityWrapTargets[0].parentElement
    if (!grid) return
    const anyVisible = this.identityWrapTargets.some(
      (wrap) => !wrap.classList.contains('hidden'),
    )
    grid.classList.toggle('hidden', !anyVisible)
  }

  _syncNameSpan() {
    const wraps = ['first_name', 'last_name']
      .map((field) =>
        this.identityWrapTargets.find(
          (wrap) => wrap.dataset.identityField === field,
        ),
      )
      .filter(Boolean)
    const visible = wraps.filter((wrap) => !wrap.classList.contains('hidden'))
    wraps.forEach((wrap) => {
      wrap.classList.toggle(
        'sm:col-span-2',
        visible.length === 1 && visible[0] === wrap,
      )
    })
  }

  _inputFor(wrap) {
    if (this.hasIdentityInputTarget) {
      const target = this.identityInputTargets.find((input) =>
        wrap.contains(input),
      )
      if (target) return target
    }
    return wrap.querySelector('input')
  }

  _isSomeoneElse() {
    return this._selectedUuid().length === 0
  }

  _selectedUuid() {
    if (!this.hasDropdownTarget) return ''
    return this.dropdownTarget.value || ''
  }

  _isSelf() {
    const sentinel = this.selfSentinelValue
    return sentinel.length > 0 && this._selectedUuid() === sentinel
  }

  _buyerValues() {
    const values = {}
    document
      .querySelectorAll('[data-buyer-identity-field]')
      .forEach((el) => (values[el.dataset.buyerIdentityField] = el.value))
    return values
  }

  _selectedValues() {
    if (this._isSelf()) return this._buyerValues()
    const uuid = this._selectedUuid()
    if (!uuid) return {}
    return this.fieldValuesValue[uuid] || {}
  }
}
